import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import SweetAlert from 'react-bootstrap-sweetalert';
import Select from 'react-select';
import useProjectFloorLayoutV2, { EditableUnitV2, PaletteSvgV2 } from './hooks/useProjectFloorLayoutV2';
import AdminPage from '../../components/admin/AdminPage';

/* ── Status configurations ── */
const STATUS_COLORS: Record<string, string> = {
  available: '#00b66b',
  coming_soon: '#ff732b',
  sold: '#ea1b2b',
  unavailable: '#757575',
};

const STATUS_LABELS: Record<string, string> = {
  available: 'Disponible',
  coming_soon: 'Próximamente',
  sold: 'Agotado',
  unavailable: 'No Disponible',
};

type ViewMode = 'editor' | 'preview';

const InvestorProjectFloorLayoutV2 = () => {
  const { id = '', id_dist = '' } = useParams();

  const {
    floorInfo,
    placedUnits,
    setPlacedUnits,
    svgPalette,
    setSvgPalette,
    selectedUnitId,
    setSelectedUnitId,
    availableUnits,
    loadFloorLayout,
    handleUploadSvgFile,
    handleUploadBackground,
    handleSave,
    isLoading,
  } = useProjectFloorLayoutV2();

  /* ── UI States ── */
  const [mode, setMode] = useState<ViewMode>('editor');
  const [hoveredUnitId, setHoveredUnitId] = useState<string | null>(null);
  const [activeUnitId, setActiveUnitId] = useState<string | null>(null);
  const [isUploadingSvg, setIsUploadingSvg] = useState<boolean>(false);

  const [canvasWidth, setCanvasWidth] = useState(1200);
  const [canvasHeight, setCanvasHeight] = useState(800);
  const [zoom, setZoom] = useState<number>(100);
  const [draggingUnitId, setDraggingUnitId] = useState<string | null>(null);
  const [bgTimestamp, setBgTimestamp] = useState<number>(Date.now());

  const bgUrl = floorInfo?.level_facade_background
    ? `${floorInfo.level_facade_background}?t=${bgTimestamp}`
    : null;

  const [sweetAlert, setSweetAlert] = useState<boolean>(false);
  const [alertProps, setAlertProps] = useState<any>({
    title: '',
    onConfirm: () => setSweetAlert(false),
  });

  const dragOffset = useRef<{ dx: number; dy: number }>({ dx: 0, dy: 0 });

  const handleConfirmSave = () => {
    setAlertProps({
      warning: true,
      title: 'Guardar configuración',
      text: '¿Estás seguro de que deseas guardar la configuración de unidades para este piso?',
      showCancel: true,
      confirmBtnBsStyle: 'success',
      confirmBtnText: 'Guardar',
      cancelBtnBsStyle: 'danger',
      cancelBtnText: 'Cancelar',
      onConfirm: async () => {
        setSweetAlert(false);
        const success = await handleSave(placedUnits);
        if (success) {
          setAlertProps({
            success: true,
            title: '¡Guardado!',
            text: 'La configuración de unidades del piso se ha guardado correctamente.',
            confirmBtnBsStyle: 'success',
            confirmBtnText: 'Aceptar',
            onConfirm: () => setSweetAlert(false),
          });
          setSweetAlert(true);
        } else {
          setAlertProps({
            danger: true,
            title: 'Error',
            text: 'Ocurrió un error al guardar la configuración del plano de piso.',
            confirmBtnBsStyle: 'danger',
            confirmBtnText: 'Entiendo',
            onConfirm: () => setSweetAlert(false),
          });
          setSweetAlert(true);
        }
      },
      onCancel: () => setSweetAlert(false),
    });
    setSweetAlert(true);
  };

  const svgRef = useRef<SVGSVGElement>(null);
  const bgInputRef = useRef<HTMLInputElement>(null);
  const svgInputRef = useRef<HTMLInputElement>(null);
  const coordsInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadFloorLayout();
  }, [loadFloorLayout]);

  // Read natural image dimensions when background loads
  useEffect(() => {
    if (bgUrl) {
      const img = new Image();
      img.onload = () => {
        const w = img.naturalWidth || 1200;
        const h = img.naturalHeight || 800;
        setCanvasWidth(w);
        setCanvasHeight(h);
        const defaultZoom = Math.min(100, Math.round((600 / w) * 100));
        setZoom(defaultZoom);
      };
      img.src = bgUrl;
    }
  }, [bgUrl]);

  // SVG Coordinates Conversion Helper
  const clientToSvgCoords = (
    svgEl: SVGSVGElement,
    clientX: number,
    clientY: number,
  ): { x: number; y: number } => {
    const pt = svgEl.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const ctm = svgEl.getScreenCTM()?.inverse();
    if (!ctm) return { x: 0, y: 0 };
    const svgPt = pt.matrixTransform(ctm);
    return { x: Math.round(svgPt.x * 10) / 10, y: Math.round(svgPt.y * 10) / 10 };
  };

  /* ── Drag & Drop Handlers ── */
  const handlePaletteDragStart = (svgId: string, e: React.DragEvent) => {
    e.dataTransfer.setData('application/x-palette-svg-id', svgId);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleCanvasDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleCanvasDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const svgId = e.dataTransfer.getData('application/x-palette-svg-id');
    if (!svgId || !svgRef.current) return;

    const paletteSvg = svgPalette.find((s) => s.id === svgId);
    if (!paletteSvg) return;

    const pos = clientToSvgCoords(svgRef.current, e.clientX, e.clientY);
    const x = pos.x - paletteSvg.width / 2;
    const y = pos.y - paletteSvg.height / 2;

    const newUnit: EditableUnitV2 = {
      id: crypto.randomUUID(),
      unit_id: '',
      unit_name: '',
      model_name: '',
      x: Math.round(x * 10) / 10,
      y: Math.round(y * 10) / 10,
      svgWidth: paletteSvg.width || 100,
      svgHeight: paletteSvg.height || 100,
      svgViewBox: paletteSvg.viewBox || '0 0 100 100',
      path: paletteSvg.pathD,
      fileName: paletteSvg.fileName,
      svg_url: paletteSvg.svgUrl,
      placed: true,
      isNew: true,
    };

    setPlacedUnits((prev) => [...prev, newUnit]);
    setSelectedUnitId(newUnit.id);
    setSvgPalette((prev) => prev.filter((s) => s.id !== svgId));
  };

  const startUnitDrag = (unitId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!svgRef.current) return;

    const unit = placedUnits.find((u) => u.id === unitId);
    if (!unit) return;

    const svgPt = clientToSvgCoords(svgRef.current, e.clientX, e.clientY);
    dragOffset.current = {
      dx: svgPt.x - unit.x,
      dy: svgPt.y - unit.y,
    };

    setDraggingUnitId(unitId);
    setSelectedUnitId(unitId);
  };

  const handleUnitDrag = (e: React.MouseEvent) => {
    if (!draggingUnitId || !svgRef.current) return;

    const svgPt = clientToSvgCoords(svgRef.current, e.clientX, e.clientY);
    const newX = Math.round((svgPt.x - dragOffset.current.dx) * 10) / 10;
    const newY = Math.round((svgPt.y - dragOffset.current.dy) * 10) / 10;

    setPlacedUnits((prev) =>
      prev.map((u) => (u.id === draggingUnitId ? { ...u, x: newX, y: newY } : u)),
    );
  };

  const endUnitDrag = () => {
    setDraggingUnitId(null);
  };

  const handleUpdateProperty = (
    unitId: string,
    key: keyof EditableUnitV2,
    value: any,
  ) => {
    setPlacedUnits((prev) =>
      prev.map((u) => {
        if (u.id !== unitId) return u;
        return { ...u, [key]: value };
      }),
    );
  };

  const handleMapUnitId = (unitId: string, selectedOption: any) => {
    const modelUnitId = selectedOption?.value || '';
    const matchedUnit = availableUnits.find((u) => u.id === modelUnitId);
    if (matchedUnit) {
      const unconfiguredLayout = placedUnits.find(
        (u) => u.unit_id === modelUnitId && (!u.svg_url || !u.path)
      );

      setPlacedUnits((prev) => {
        let list = prev.map((u) =>
          u.id === unitId
            ? {
              ...u,
              id: unconfiguredLayout ? unconfiguredLayout.id : u.id,
              isNew: unconfiguredLayout ? false : u.isNew,
              unit_id: modelUnitId,
              unit_name: matchedUnit.name,
              model_name: matchedUnit.model_name || matchedUnit.projectModel?.name || '',
              status: matchedUnit.status,
            }
            : u
        );

        if (unconfiguredLayout) {
          list = list.filter((u) => u.id !== unconfiguredLayout.id || u.id === unitId);
        }
        return list;
      });
    } else {
      setPlacedUnits((prev) =>
        prev.map((u) =>
          u.id === unitId
            ? {
              ...u,
              unit_id: '',
              unit_name: '',
              model_name: '',
              status: undefined,
            }
            : u
        )
      );
    }
  };

  const handleRemoveUnit = (unitId: string) => {
    const unit = placedUnits.find((u) => u.id === unitId);
    if (!unit) return;

    const paletteSvg: PaletteSvgV2 = {
      id: crypto.randomUUID(),
      fileName: unit.fileName,
      viewBox: unit.svgViewBox,
      width: unit.svgWidth,
      height: unit.svgHeight,
      pathD: unit.path,
      svgUrl: unit.svg_url,
    };

    setSvgPalette((prev) => [...prev, paletteSvg]);
    setPlacedUnits((prev) => prev.filter((u) => u.id !== unitId));
    if (selectedUnitId === unitId) setSelectedUnitId(null);
  };

  const handleSvgInputUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setIsUploadingSvg(true);
      try {
        const filesArray = Array.from(e.target.files);
        await Promise.all(filesArray.map((file) => handleUploadSvgFile(file)));
      } catch (error) {
        console.error('Error uploading SVGs:', error);
      } finally {
        setIsUploadingSvg(false);
      }
    }
    if (svgInputRef.current) svgInputRef.current.value = '';
  };

  const handleBgInputUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const res = await handleUploadBackground(file);
      if (res) {
        setBgTimestamp(Date.now());
      }
    }
    if (bgInputRef.current) bgInputRef.current.value = '';
  };

  const handleCoordsUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const coordsMap = parseCoordinatesFile(text);

      let updatedCount = 0;
      let placedCount = 0;

      // Helper function to normalize keys for comparison
      const normalizeKey = (str: string): string => {
        return str
          .toLowerCase()
          .replace(/\.svg$/i, '')
          .replace(/[\s\-_]+/g, '')
          .replace(/0+(\d+)/g, '$1');
      };

      // Helper to extract a number from a string
      const extractNumber = (str: string): number | null => {
        const match = str.match(/\d+/);
        return match ? parseInt(match[0], 10) : null;
      };

      // Create a map of normalized coordinate keys and numeric coordinate keys
      const normalizedCoords: Record<string, { x: number; y: number }> = {};
      const numericCoords: Record<number, { x: number; y: number }> = {};

      Object.entries(coordsMap).forEach(([rawKey, coords]) => {
        const norm = normalizeKey(rawKey);
        normalizedCoords[norm] = coords;
        
        const num = extractNumber(rawKey);
        if (num !== null) {
          numericCoords[num] = coords;
        }
      });

      // 1. Update coordinates of already placed units
      const newPlacedUnits = placedUnits.map((unit) => {
        const fileKeyNorm = normalizeKey(unit.fileName);
        const nameKeyNorm = unit.unit_name ? normalizeKey(unit.unit_name) : '';
        const unitNum = unit.unit_name ? extractNumber(unit.unit_name) : null;
        
        // Match by normalized filename, normalized unit name, or unit number
        const match =
          normalizedCoords[fileKeyNorm] ||
          (nameKeyNorm ? normalizedCoords[nameKeyNorm] : null) ||
          (unitNum !== null ? numericCoords[unitNum] : null);

        if (match) {
          updatedCount++;
          return {
            ...unit,
            x: match.x,
            y: match.y,
          };
        }
        return unit;
      });

      // 2. Find matching SVGs in the palette and place them on the canvas
      const newPalette = [...svgPalette];
      const placedFromPalette: EditableUnitV2[] = [];

      for (let i = newPalette.length - 1; i >= 0; i--) {
        const paletteSvg = newPalette[i];
        const fileKeyNorm = normalizeKey(paletteSvg.fileName);
        const unitNum = extractNumber(paletteSvg.fileName);
        
        // Match by normalized filename or extracted unit number
        const match =
          normalizedCoords[fileKeyNorm] ||
          (unitNum !== null ? numericCoords[unitNum] : null);
        
        if (match) {
          placedCount++;
          newPalette.splice(i, 1);
          placedFromPalette.push({
            id: crypto.randomUUID(),
            unit_id: '',
            unit_name: '',
            model_name: '',
            x: match.x,
            y: match.y,
            svgWidth: paletteSvg.width || 100,
            svgHeight: paletteSvg.height || 100,
            svgViewBox: paletteSvg.viewBox || '0 0 100 100',
            path: paletteSvg.pathD,
            fileName: paletteSvg.fileName,
            svg_url: paletteSvg.svgUrl,
            placed: true,
            isNew: true,
          });
        }
      }

      setPlacedUnits([...newPlacedUnits, ...placedFromPalette]);
      setSvgPalette(newPalette);

      alert(`Coordenadas aplicadas:\n- ${updatedCount} unidades actualizadas\n- ${placedCount} unidades colocadas desde la paleta`);
    } catch (err) {
      console.error(err);
      alert('Error al leer el archivo de coordenadas');
    }

    if (coordsInputRef.current) coordsInputRef.current.value = '';
  };

  const selectedUnit = placedUnits.find((u) => u.id === selectedUnitId);
  const activeUnit = placedUnits.find((u) => u.id === activeUnitId || u.id === hoveredUnitId);

  // Filter out units that are already assigned to other placed SVGs,
  // but keep the unit currently assigned to the selected SVG (if any).
  const assignedUnitIds = placedUnits
    .map((pu) => pu.unit_id)
    .filter((uid) => uid && uid !== selectedUnit?.unit_id);

  // Available options for mapping react-select
  const selectOptions = availableUnits
    .filter((u: any) => !assignedUnitIds.includes(u.id))
    .map((u: any) => ({
      value: u.id,
      label: `${u.name} - ${u.model_name || u.projectModel?.name || ''}`,
    }));

  const activeSelectOption = selectedUnit?.unit_id
    ? selectOptions.find((opt) => opt.value === selectedUnit.unit_id)
    : null;

  // Render Full Screen Loading Spinner if loading and floorInfo not ready
  if (isLoading && !floorInfo) {
    return (
      <div style={styles.loadingContainer}>
        <div className="spinner-border text-success" role="status" />
        <span style={{ marginTop: '12px', fontWeight: 600, color: '#757575' }}>
          Cargando distribución del piso...
        </span>
      </div>
    );
  }

  return (
    <AdminPage
      name={floorInfo ? `${floorInfo.project_name} - Nivel ${floorInfo.level}` : 'Cargando...'}
      parentName={'Administrador de unidades de piso'}
      parentUri={`/investors/projects/${id}/tower-layout-v2`}
    >
      <div style={styles.wrapper}>
        {sweetAlert && (
          <SweetAlert {...alertProps}>
            {alertProps.text || ''}
          </SweetAlert>
        )}

        {/* ── Top Header ── */}
        <header style={styles.header}>
          <div style={styles.modeToggle}>
            <button
              style={{
                ...styles.modeBtn,
                ...(mode === 'editor' ? styles.modeBtnActive : {}),
              }}
              onClick={() => setMode('editor')}
            >
              ✏️ Editor
            </button>
            <button
              style={{
                ...styles.modeBtn,
                ...(mode === 'preview' ? styles.modeBtnActive : {}),
              }}
              onClick={() => setMode('preview')}
            >
              👁️ Preview
            </button>
          </div>

          {/* Zoom Slider */}
          {bgUrl && (
            <div style={styles.zoomContainer}>
              <span style={styles.zoomLabel}>🔍 Zoom: {zoom}%</span>
              <input
                type="range"
                min="10"
                max="200"
                step="5"
                value={zoom}
                onChange={(e) => setZoom(parseInt(e.target.value, 10))}
                style={styles.zoomSlider}
              />
            </div>
          )}

          <div style={styles.headerActions}>
            <Link
              to={`/investors/projects/${id}/tower-layout-v2`}
              className="btn btn-outline-secondary btn-sm"
              style={styles.backBtn}
            >
              ◀ Volver a la Torre
            </Link>
            <button
              style={styles.saveBtn}
              onClick={handleConfirmSave}
              disabled={isLoading || !bgUrl}
            >
              💾 Guardar
            </button>
          </div>
        </header>

        {/* ── Main Viewport ── */}
        {!floorInfo ? (
          <div style={styles.emptyStateContainer}>
            <div style={styles.emptyIcon}>⚠️</div>
            <div style={styles.emptyTitle}>Error al cargar los datos del piso</div>
            <div style={styles.emptySub}>
              No pudimos obtener la configuración de la base de datos. Por favor, asegúrate de que el backend esté disponible y tenga las migraciones aplicadas.
            </div>
            <Link
              to={`/investors/projects/${id}/tower-layout-v2`}
              className="btn btn-success btn-sm"
              style={styles.uploadMainBtn}
            >
              Volver a la Torre
            </Link>
          </div>
        ) : !bgUrl ? (
          <div style={styles.emptyStateContainer}>
            <div style={styles.emptyIcon}>🖼️</div>
            <div style={styles.emptyTitle}>Sube la imagen de fondo del piso</div>
            <div style={styles.emptySub}>Por favor sube la imagen de fondo para empezar a plotiar unidades.</div>
            <input
              type="file"
              ref={bgInputRef}
              onChange={handleBgInputUpload}
              accept="image/*"
              style={{ display: 'none' }}
              id="bg-upload-floor-main"
            />
            <button
              style={styles.uploadMainBtn}
              onClick={() => document.getElementById('bg-upload-floor-main')?.click()}
            >
              Subir Imagen de Fondo
            </button>
          </div>
        ) : (
          <div style={styles.editorBody}>
            {/* ══════════════════════════════════════════════
              EDITOR MODE
              ══════════════════════════════════════════════ */}
            {mode === 'editor' && (
              <>
                {/* Left Sidebar Palette */}
                <aside style={styles.sidebarLeft}>
                  {/* Background image preview & change */}
                  <div style={styles.sidebarSection}>
                    <div style={styles.sectionTitle}>🖼️ Imagen de piso</div>
                    <div style={styles.thumbWrap}>
                      <img src={bgUrl} alt="Piso" style={styles.thumbImg} />
                      <button
                        style={styles.changeBtn}
                        onClick={() => bgInputRef.current?.click()}
                      >
                        Cambiar
                      </button>
                      <input
                        ref={bgInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleBgInputUpload}
                      />
                    </div>
                  </div>

                  {/* SVG Upload */}
                  <div style={styles.sidebarSection}>
                    <div style={styles.sectionTitle}>📐 SVGs de unidades</div>
                    <input
                      ref={svgInputRef}
                      type="file"
                      accept=".svg"
                      multiple
                      style={{ display: 'none' }}
                      onChange={handleSvgInputUpload}
                    />
                    <input
                      ref={coordsInputRef}
                      type="file"
                      accept=".txt"
                      style={{ display: 'none' }}
                      onChange={handleCoordsUpload}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <button
                        style={{
                          ...styles.uploadBtn,
                          opacity: isUploadingSvg ? 0.7 : 1,
                          cursor: isUploadingSvg ? 'not-allowed' : 'pointer',
                        }}
                        onClick={() => !isUploadingSvg && svgInputRef.current?.click()}
                        disabled={isUploadingSvg}
                      >
                        {isUploadingSvg ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm"
                              role="status"
                              aria-hidden="true"
                              style={{ width: '14px', height: '14px', marginRight: '6px' }}
                            />
                            Subiendo SVGs...
                          </>
                        ) : (
                          <>
                            <span style={styles.uploadIcon}>+</span>
                            Subir archivos SVG
                          </>
                        )}
                      </button>
                      <button
                        style={{
                          ...styles.uploadBtn,
                          border: '2px dashed #d0d0d0',
                        }}
                        onClick={() => coordsInputRef.current?.click()}
                      >
                        🗺️ Cargar Coordenadas (.txt)
                      </button>
                    </div>
                  </div>

                  {/* SVG Palette Items */}
                  {svgPalette.length > 0 && (
                    <div style={{ ...styles.sidebarSection, flex: 1, overflowY: 'auto' }}>
                      <div style={styles.sectionTitle}>Arrastra al lienzo ({svgPalette.length})</div>
                      <div style={styles.svgList}>
                        {svgPalette.map((svg) => (
                          <div
                            key={svg.id}
                            draggable
                            onDragStart={(e) => handlePaletteDragStart(svg.id, e)}
                            style={styles.svgItem}
                          >
                            <svg
                              viewBox={svg.viewBox}
                              style={styles.svgMini}
                            >
                              <path d={svg.pathD} fill="#e2e8f0" stroke="#94a3b8" />
                            </svg>
                            <div style={styles.svgItemInfo}>
                              <span style={styles.svgItemName}>{svg.fileName}</span>
                              <span style={styles.svgItemDim}>
                                {Math.round(svg.width)}×{Math.round(svg.height)}
                              </span>
                            </div>
                            <button
                              style={styles.removePaletteBtn}
                              onClick={() => setSvgPalette((prev) => prev.filter((s) => s.id !== svg.id))}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </aside>

                {/* Center Canvas */}
                <div
                  style={styles.canvasWrap}
                  onDragOver={handleCanvasDragOver}
                  onDrop={handleCanvasDrop}
                >
                  <div style={styles.svgContainer}>
                    <svg
                      ref={svgRef}
                      viewBox={`0 0 ${canvasWidth} ${canvasHeight}`}
                      preserveAspectRatio="xMidYMid meet"
                      style={{
                        ...styles.canvasSvg,
                        cursor: draggingUnitId ? 'grabbing' : 'default',
                        width: `${(canvasWidth * zoom) / 100}px`,
                        height: `${(canvasHeight * zoom) / 100}px`,
                      }}
                      onMouseMove={handleUnitDrag}
                      onMouseUp={endUnitDrag}
                      onMouseLeave={endUnitDrag}
                      onClick={() => setSelectedUnitId(null)}
                    >
                      <image
                        href={bgUrl}
                        x={0}
                        y={0}
                        width={canvasWidth}
                        height={canvasHeight}
                        preserveAspectRatio="none"
                      />

                      {/* Placed SVG units */}
                      {placedUnits.filter((unit) => unit.placed).map((unit) => {
                        const isSelected = selectedUnitId === unit.id;
                        const color = unit.status ? STATUS_COLORS[unit.status] : '#3b82f6';
                        return (
                          <svg
                            key={unit.id}
                            x={unit.x}
                            y={unit.y}
                            width={unit.svgWidth}
                            height={unit.svgHeight}
                            viewBox={unit.svgViewBox}
                            preserveAspectRatio="none"
                            overflow="visible"
                          >
                            <rect
                              x={0}
                              y={0}
                              width={unit.svgWidth}
                              height={unit.svgHeight}
                              fill="transparent"
                              style={{ cursor: draggingUnitId === unit.id ? 'grabbing' : 'grab' }}
                              onMouseDown={(e) => startUnitDrag(unit.id, e)}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedUnitId(unit.id);
                              }}
                            />
                            <path
                              d={unit.path}
                              fill={color}
                              fillOpacity={isSelected ? 0.45 : 0.2}
                              stroke={color}
                              strokeWidth={1.2}
                              style={{
                                pointerEvents: 'none',
                                filter: isSelected ? `drop-shadow(0 0 6px ${color}99)` : 'none',
                              }}
                            />
                            <text
                              x={unit.svgWidth / 2}
                              y={unit.svgHeight / 2 + 4}
                              textAnchor="middle"
                              fill={isSelected ? '#212121' : '#ffffff'}
                              fontSize="10px"
                              fontWeight="800"
                              style={{
                                pointerEvents: 'none',
                                textShadow: '0 1px 3px rgba(0,0,0,0.6)',
                              }}
                            >
                              {unit.unit_name || '...'}
                            </text>
                          </svg>
                        );
                      })}
                    </svg>
                  </div>
                </div>

                {/* Right Sidebar Properties */}
                <aside style={styles.sidebarRight}>
                  <div style={styles.sidebarSection}>
                    <div style={styles.sectionTitle}>Unidades ploteadas ({placedUnits.filter((u) => u.placed).length})</div>
                    <div style={styles.floorListWrap}>
                      {placedUnits.filter((u) => u.placed).map((u) => {
                        const isSel = selectedUnitId === u.id;
                        const color = u.status ? STATUS_COLORS[u.status] : '#3b82f6';
                        return (
                          <button
                            key={u.id}
                            onClick={() => setSelectedUnitId(isSel ? null : u.id)}
                            style={{
                              ...styles.floorItemBtn,
                              background: isSel ? `${color}18` : 'transparent',
                              borderLeft: `3px solid ${isSel ? color : 'transparent'}`,
                            }}
                          >
                            <span style={{ ...styles.floorItemDot, background: color }} />
                            <span style={styles.floorItemLabel}>{u.unit_name || 'Sin vincular'}</span>
                            <span style={styles.floorItemPos}>({Math.round(u.x)}, {Math.round(u.y)})</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {selectedUnit ? (
                    <div style={styles.sidebarSection}>
                      <div style={styles.propsHeader}>
                        <div style={styles.propsLabel}>Propiedades</div>
                        <div style={{ ...styles.propsFloor, color: selectedUnit.status ? STATUS_COLORS[selectedUnit.status] : '#3b82f6' }}>
                          Unidad: {selectedUnit.unit_name || 'Sin vincular'}
                        </div>
                      </div>

                      {/* Autocomplete selector */}
                      <div style={{ marginBottom: '14px' }}>
                        <span style={styles.propRowLabel}>Vincular Unidad del Proyecto</span>
                        <Select
                          options={selectOptions}
                          value={activeSelectOption}
                          onChange={(opt) => handleMapUnitId(selectedUnit.id, opt)}
                          placeholder="Buscar unidad..."
                          isClearable
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              minHeight: '34px',
                              borderRadius: '6px',
                              borderColor: '#eeeeee',
                              fontSize: '12px',
                            }),
                          }}
                        />
                      </div>

                      {/* Sliders */}
                      <PropRow
                        label="Posición X"
                        value={selectedUnit.x}
                        min={-100}
                        max={canvasWidth}
                        step={5}
                        onChange={(v) => handleUpdateProperty(selectedUnit.id, 'x', v)}
                        color={selectedUnit.status ? STATUS_COLORS[selectedUnit.status] : '#3b82f6'}
                      />

                      <PropRow
                        label="Posición Y"
                        value={selectedUnit.y}
                        min={-100}
                        max={canvasHeight}
                        step={5}
                        onChange={(v) => handleUpdateProperty(selectedUnit.id, 'y', v)}
                        color={selectedUnit.status ? STATUS_COLORS[selectedUnit.status] : '#3b82f6'}
                      />



                      <button
                        style={styles.removeFloorBtn}
                        onClick={() => handleRemoveUnit(selectedUnit.id)}
                      >
                        🗑️ Quitar del lienzo
                      </button>
                    </div>
                  ) : (
                    <div style={styles.emptyHint}>
                      Selecciona una unidad en el lienzo o en la lista superior para configurar sus propiedades.
                    </div>
                  )}
                </aside>
              </>
            )}

            {/* ══════════════════════════════════════════════
              PREVIEW MODE
              ══════════════════════════════════════════════ */}
            {mode === 'preview' && (
              <div style={styles.viewerContainer}>
                {/* Main Interactive Canvas */}
                <div style={styles.viewerMain}>
                  <div style={styles.viewerBuildingContainer}>
                    <svg
                      viewBox={`0 0 ${canvasWidth} ${canvasHeight}`}
                      preserveAspectRatio="xMidYMid meet"
                      style={{
                        ...styles.viewerSvg,
                        width: `${(canvasWidth * zoom) / 100}px`,
                        height: `${(canvasHeight * zoom) / 100}px`,
                      }}
                    >
                      <image
                        href={bgUrl}
                        x={0}
                        y={0}
                        width={canvasWidth}
                        height={canvasHeight}
                        preserveAspectRatio="none"
                      />
                      {placedUnits.filter((unit) => unit.placed).map((unit) => {
                        const isHover = hoveredUnitId === unit.id;
                        const isActive = activeUnitId === unit.id;
                        const color = unit.status ? STATUS_COLORS[unit.status] : '#757575';
                        const highlight = isHover || isActive;

                        return (
                          <svg
                            key={unit.id}
                            x={unit.x}
                            y={unit.y}
                            width={unit.svgWidth}
                            height={unit.svgHeight}
                            viewBox={unit.svgViewBox}
                            preserveAspectRatio="none"
                            overflow="visible"
                          >
                            <path
                              d={unit.path}
                              fill={color}
                              fillOpacity={highlight ? 0.45 : 0.15}
                              stroke={color}
                              strokeOpacity={highlight ? 1 : 0.4}
                              strokeWidth={highlight ? 2.5 : 1}
                              style={{
                                cursor: 'pointer',
                                transition: 'fill-opacity 0.25s, stroke-opacity 0.25s',
                                filter: highlight ? `drop-shadow(0 0 6px ${color}88)` : 'none',
                              }}
                              onMouseEnter={() => setHoveredUnitId(unit.id)}
                              onMouseLeave={() => setHoveredUnitId(null)}
                              onClick={() => setActiveUnitId((prev) => (prev === unit.id ? null : unit.id))}
                            />
                          </svg>
                        );
                      })}
                    </svg>

                    {/* Active Unit Overlay Card */}
                    {activeUnit && (
                      <div style={styles.infoCard}>
                        <div style={styles.infoCardHeader}>
                          <div>
                            <div style={styles.infoCardLevelLabel}>Unidad</div>
                            <div style={styles.infoCardLevelNum}>
                              {activeUnit.unit_name || 'Sin vincular'}
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              setActiveUnitId(null);
                              setHoveredUnitId(null);
                            }}
                            style={styles.infoCardClose}
                          >
                            ✕
                          </button>
                        </div>

                        {activeUnit.status ? (
                          <div
                            style={{
                              ...styles.infoCardBadge,
                              background: `${STATUS_COLORS[activeUnit.status]}22`,
                              color: STATUS_COLORS[activeUnit.status],
                            }}
                          >
                            <span
                              style={{
                                ...styles.infoCardDot,
                                background: STATUS_COLORS[activeUnit.status],
                              }}
                            />
                            {STATUS_LABELS[activeUnit.status] || activeUnit.status}
                          </div>
                        ) : (
                          <div style={styles.unlinkedBadge}>No Vinculada</div>
                        )}

                        <div style={styles.infoCardStatRow}>
                          <span style={styles.infoCardStatLabel}>Modelo</span>
                          <span style={styles.infoCardStatValue}>
                            {activeUnit.model_name || '-'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right sidebar listing units */}
                <aside style={styles.viewerSide}>
                  <div style={styles.viewerSideTitle}>Unidades Ploteadas</div>
                  <div style={styles.viewerSideList}>
                    {placedUnits.filter((u) => u.placed).length === 0 ? (
                      <div style={styles.emptySideHint}>Ninguna unidad ploteada.</div>
                    ) : (
                      placedUnits.filter((u) => u.placed).map((unit) => {
                        const color = unit.status ? STATUS_COLORS[unit.status] : '#757575';
                        const isSelected = activeUnitId === unit.id;

                        return (
                          <button
                            key={unit.id}
                            onClick={() => setActiveUnitId(isSelected ? null : unit.id)}
                            style={{
                              ...styles.viewerSideItem,
                              background: isSelected ? '#f5f5f5' : 'transparent',
                              borderLeftColor: isSelected ? color : 'transparent',
                            }}
                          >
                            <span style={{ ...styles.viewerSideDot, background: color }} />
                            <div style={styles.viewerSideText}>
                              <span style={styles.viewerSideLevelNum}>
                                {unit.unit_name || 'Sin vincular'}
                              </span>
                              <span style={styles.viewerSideStatus}>
                                {unit.model_name || 'Molde SVG'}
                              </span>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </aside>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminPage>
  );
};

/* ── Coordinates Parser Helper ── */
const parseCoordinatesFile = (text: string): Record<string, { x: number; y: number }> => {
  const result: Record<string, { x: number; y: number }> = {};
  const lines = text.split(/\r?\n/);
  const coordRegex = /(?:[xy][:=]?)?\s*([-\d.]+)\s+(?:[xy][:=]?)?\s*([-\d.]+)\s*$/i;
  
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    
    const coordMatch = trimmed.match(coordRegex);
    if (coordMatch) {
      const x = parseFloat(coordMatch[1]);
      const y = parseFloat(coordMatch[2]);
      if (!isNaN(x) && !isNaN(y)) {
        const idPart = trimmed.substring(0, coordMatch.index).trim();
        const cleanId = idPart.replace(/\s*(?:[xy][:=]?)?\s*$/, '').trim();
        if (cleanId) {
          result[cleanId.toLowerCase()] = { x, y };
        }
      }
    }
  }
  return result;
};

/* ── PropRow component (inline helper) ── */
interface PropRowProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit?: string;
  onChange: (v: number) => void;
  color?: string;
}

const PropRow: React.FC<PropRowProps> = ({
  label,
  value,
  min,
  max,
  step,
  unit = 'px',
  onChange,
  color = '#00b66b',
}) => {
  const [inputValue, setInputValue] = useState<string>(String(Math.round(value * 10) / 10));

  useEffect(() => {
    setInputValue(String(Math.round(value * 10) / 10));
  }, [value]);

  const nudge = (delta: number) => {
    const next = Math.round((value + delta) * 10) / 10;
    const bounded = Math.max(min, Math.min(max, next));
    onChange(bounded);
    setInputValue(String(bounded));
  };

  const handleInputChange = (val: string) => {
    setInputValue(val);
    const parsed = parseFloat(val);
    if (!isNaN(parsed)) {
      onChange(Math.max(min, Math.min(max, parsed)));
    }
  };

  const handleBlur = () => {
    setInputValue(String(Math.round(value * 10) / 10));
  };

  return (
    <div style={propStyles.row}>
      <div style={propStyles.labelRow}>
        <span style={propStyles.label}>{label}</span>
        <span style={propStyles.valueDisplay}>
          {Math.round(value * 10) / 10}
          <span style={propStyles.unit}>{unit}</span>
        </span>
      </div>

      <div style={propStyles.sliderRow}>
        <button type="button" style={propStyles.nudgeBtn} onClick={() => nudge(-step)}>
          ◀
        </button>
        <input
          type="range"
          min={min}
          max={max}
          step={step * 0.1}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          style={{ ...propStyles.slider, accentColor: color }}
        />
        <button type="button" style={propStyles.nudgeBtn} onClick={() => nudge(step)}>
          ▶
        </button>
      </div>

      <input
        type="number"
        min={min}
        max={max}
        step={step * 0.1}
        value={inputValue}
        onChange={(e) => handleInputChange(e.target.value)}
        onBlur={handleBlur}
        style={propStyles.numInput}
      />
    </div>
  );
};

/* ── Inline CSS Styles ── */
const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 200px)',
    minHeight: '650px',
    background: '#f5f5f5',
    fontFamily: "'Nunito', 'Segoe UI', sans-serif",
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '52px',
    padding: '0 24px',
    background: '#ffffff',
    borderBottom: '1px solid #eeeeee',
    flexShrink: 0,
    boxShadow: '0 2px 5px rgba(0,0,0,0.02)',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: 800,
    fontSize: '16px',
    color: '#00b66b',
  },
  levelBreadcrumb: {
    color: '#9e9e9e',
    fontWeight: 600,
  },
  logoIcon: {
    width: '26px',
    height: '26px',
    background: '#e5fff5',
    borderRadius: '7px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modeToggle: {
    display: 'flex',
    background: '#f0f0f0',
    borderRadius: '8px',
    padding: '2px',
    gap: '2px',
  },
  modeBtn: {
    border: 'none',
    borderRadius: '6px',
    padding: '5px 14px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    background: 'transparent',
    color: '#757575',
    transition: 'all 0.2s',
  },
  modeBtnActive: {
    background: '#ffffff',
    color: '#212121',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  headerActions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  backBtn: {
    borderRadius: '8px',
    padding: '5px 14px',
    fontSize: '12px',
    fontWeight: 600,
  },
  saveBtn: {
    border: '1px solid #00b66b',
    borderRadius: '8px',
    padding: '5px 16px',
    fontSize: '12px',
    fontWeight: 700,
    cursor: 'pointer',
    background: '#00b66b',
    color: '#ffffff',
    transition: 'background 0.2s',
  },
  editorBody: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    minHeight: 0,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    background: '#f5f5f5',
  },
  sidebarLeft: {
    width: '230px',
    flexShrink: 0,
    background: '#ffffff',
    borderRight: '1px solid #eeeeee',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  sidebarSection: {
    padding: '14px 16px 10px',
    borderBottom: '1px solid #f0f0f0',
  },
  sectionTitle: {
    fontSize: '10px',
    fontWeight: 700,
    color: '#9e9e9e',
    textTransform: 'uppercase',
    letterSpacing: '1.2px',
    marginBottom: '10px',
  },
  thumbWrap: {
    position: 'relative',
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid #eeeeee',
  },
  thumbImg: {
    width: '100%',
    height: '100px',
    objectFit: 'cover',
    display: 'block',
  },
  changeBtn: {
    position: 'absolute',
    bottom: '4px',
    right: '4px',
    background: 'rgba(0,0,0,0.6)',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '3px 8px',
    fontSize: '10px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  uploadBtn: {
    width: '100%',
    padding: '12px 10px',
    background: '#f5f5f5',
    border: '2px dashed #d0d0d0',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
    color: '#616161',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    transition: 'border-color 0.2s, background 0.2s',
  },
  uploadIcon: {
    fontSize: '18px',
    fontWeight: 800,
    color: '#00b66b',
    lineHeight: 1,
  },
  svgList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  svgItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 8px',
    borderRadius: '8px',
    background: '#f9f9f9',
    border: '1px solid #eeeeee',
    cursor: 'grab',
    transition: 'box-shadow 0.15s, border-color 0.15s',
  },
  svgMini: {
    width: '36px',
    height: '24px',
    flexShrink: 0,
    background: '#f0f0f0',
    borderRadius: '4px',
  },
  svgItemInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    minWidth: 0,
  },
  svgItemName: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#212121',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  svgItemDim: {
    fontSize: '10px',
    color: '#9e9e9e',
    fontFamily: 'monospace',
  },
  removePaletteBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '12px',
    color: '#bdbdbd',
    padding: '2px 4px',
    lineHeight: 1,
  },
  canvasWrap: {
    flex: 1,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    overflowY: 'auto',
    overflowX: 'auto',
    background: '#eaeaea',
    position: 'relative',
    height: '100%',
  },
  svgContainer: {
    position: 'relative',
    margin: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
  },
  canvasSvg: {
    display: 'block',
    borderRadius: '4px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
    background: '#fff',
  },
  infoBadge: {
    position: 'absolute',
    bottom: '24px',
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'rgba(0,0,0,0.55)',
    backdropFilter: 'blur(8px)',
    color: '#fff',
    fontSize: '11px',
    fontWeight: 600,
    padding: '5px 14px',
    borderRadius: '20px',
    fontFamily: 'monospace',
    whiteSpace: 'nowrap',
  },
  sidebarRight: {
    width: '280px',
    flexShrink: 0,
    background: '#ffffff',
    borderLeft: '1px solid #eeeeee',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  floorListWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    maxHeight: '180px',
    overflowY: 'auto',
    border: '1px solid #eeeeee',
    borderRadius: '4px',
  },
  floorItemBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 10px',
    border: 'none',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    fontSize: '12px',
    transition: 'background 0.15s',
  },
  floorItemDot: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  floorItemLabel: {
    fontWeight: 700,
    color: '#212121',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  floorItemPos: {
    fontSize: '10px',
    color: '#9e9e9e',
    fontFamily: 'monospace',
  },
  propsHeader: {
    marginBottom: '14px',
  },
  propsLabel: {
    fontSize: '10px',
    fontWeight: 700,
    color: '#9e9e9e',
    textTransform: 'uppercase',
    letterSpacing: '1.2px',
  },
  propsFloor: {
    fontSize: '14px',
    fontWeight: 800,
    marginTop: '2px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  propRowLabel: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#616161',
    display: 'block',
    marginBottom: '4px',
  },
  removeFloorBtn: {
    width: '100%',
    padding: '8px',
    border: '1px solid #ffcdd2',
    background: '#ffebee',
    color: '#c62828',
    fontSize: '11px',
    fontWeight: 700,
    borderRadius: '6px',
    cursor: 'pointer',
    marginTop: '16px',
    transition: 'background 0.2s',
  },
  emptyHint: {
    fontSize: '12px',
    color: '#9e9e9e',
    textAlign: 'center',
    padding: '24px 16px',
    lineHeight: 1.5,
  },
  emptyStateContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    color: '#9e9e9e',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '64px',
    opacity: 0.5,
    marginBottom: '10px',
  },
  emptyTitle: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#616161',
    marginBottom: '5px',
  },
  emptySub: {
    fontSize: '14px',
    maxWidth: '300px',
    lineHeight: 1.5,
    marginBottom: '20px',
  },
  uploadMainBtn: {
    padding: '10px 24px',
    background: '#00b66b',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontWeight: 700,
    cursor: 'pointer',
  },
  viewerContainer: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    height: '100%',
  },
  viewerMain: {
    flex: 1,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    padding: '16px',
    overflowY: 'auto',
    overflowX: 'auto',
    background: '#eaeaea',
  },
  viewerBuildingContainer: {
    position: 'relative',
    margin: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  viewerSvg: {
    display: 'block',
    borderRadius: '4px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
    background: '#fff',
    width: 'auto',
    maxWidth: '100%',
  },
  viewerSide: {
    width: '200px',
    flexShrink: 0,
    background: '#ffffff',
    borderLeft: '1px solid #eeeeee',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    padding: '16px 0',
    gap: '2px',
  },
  viewerSideTitle: {
    fontSize: '10px',
    fontWeight: 700,
    color: '#9e9e9e',
    textTransform: 'uppercase',
    letterSpacing: '1.2px',
    padding: '0 16px 10px',
  },
  viewerSideList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  emptySideHint: {
    fontSize: '12px',
    color: '#9e9e9e',
    textAlign: 'center',
    padding: '20px',
  },
  viewerSideItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '9px 16px',
    border: 'none',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    transition: 'background 0.15s, border-left-color 0.15s',
    borderLeft: '3px solid transparent',
  },
  viewerSideDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  viewerSideText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
  },
  viewerSideLevelNum: {
    fontSize: '13px',
    fontWeight: 700,
    color: '#212121',
  },
  viewerSideStatus: {
    fontSize: '11px',
    color: '#757575',
  },
  infoCard: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    width: '220px',
    background: '#ffffff',
    borderRadius: '14px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
    padding: '20px 18px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    zIndex: 10,
  },
  infoCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  infoCardLevelLabel: {
    fontSize: '11px',
    color: '#9e9e9e',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  infoCardLevelNum: {
    fontSize: '32px',
    fontWeight: 800,
    color: '#212121',
    lineHeight: 1,
  },
  infoCardClose: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    color: '#9e9e9e',
    padding: '4px',
    lineHeight: 1,
  },
  infoCardBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 600,
    alignSelf: 'flex-start',
  },
  infoCardDot: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  unlinkedBadge: {
    background: '#f5f5f5',
    color: '#757575',
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 600,
    alignSelf: 'flex-start',
  },
  infoCardStatRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  infoCardStatLabel: {
    fontSize: '12px',
    color: '#757575',
  },
  infoCardStatValue: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#212121',
  },
  zoomContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: '#f9f9f9',
    padding: '4px 12px',
    borderRadius: '8px',
    border: '1px solid #eeeeee',
  },
  zoomLabel: {
    fontSize: '12px',
    fontWeight: 700,
    color: '#616161',
    minWidth: '90px',
    userSelect: 'none',
  },
  zoomSlider: {
    cursor: 'pointer',
    width: '120px',
  },
};

const propStyles: Record<string, React.CSSProperties> = {
  row: {
    marginBottom: '14px',
  },
  labelRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: '4px',
  },
  label: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#616161',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  valueDisplay: {
    fontSize: '14px',
    fontWeight: 800,
    color: '#212121',
    fontFamily: 'monospace',
  },
  unit: {
    fontSize: '10px',
    fontWeight: 400,
    color: '#9e9e9e',
    marginLeft: '2px',
  },
  sliderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginBottom: '4px',
  },
  slider: {
    flex: 1,
    height: '4px',
    cursor: 'pointer',
  },
  nudgeBtn: {
    width: '24px',
    height: '24px',
    border: '1px solid #eeeeee',
    borderRadius: '6px',
    background: '#fafafa',
    cursor: 'pointer',
    fontSize: '10px',
    color: '#616161',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'background 0.15s',
  },
  numInput: {
    width: '100%',
    padding: '5px 8px',
    border: '1px solid #eeeeee',
    borderRadius: '6px',
    fontSize: '12px',
    fontFamily: 'monospace',
    fontWeight: 600,
    color: '#212121',
    background: '#fafafa',
    outline: 'none',
  },
};

export default InvestorProjectFloorLayoutV2;
